// src/pages/LabTestsDiary.tsx
import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Input, Table, message, Space, Typography, Modal, Upload, Image, DatePicker } from 'antd';
import { PlusOutlined, UploadOutlined, DownloadOutlined, EditOutlined } from '@ant-design/icons';
import { fileAPI } from '../utils/api';
import { diaryAPI } from '../utils/api';
import dayjs, { Dayjs } from 'dayjs';
import type { UploadFile } from 'antd/es/upload/interface';
import { appendUploadToFormData, getDownloadFileName, getFilePreviewUrl, getUploadRawFile } from '../utils/fileUrls';
import { formatDiaryDateTime, sortDiaryEntriesNewestFirst } from '../utils/diaryDisplay';

const { Title } = Typography;

export const LabTestsDiary: React.FC = () => {
  const [form] = Form.useForm();
  const [entries, setEntries] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [photoFile, setPhotoFile] = useState<UploadFile | null>(null);

  useEffect(() => {
    loadEntries();
  }, []);

  const resolveTestDate = (value?: Dayjs | string | null): string => {
    if (!value) {
      return dayjs().format('YYYY-MM-DD');
    }
    if (typeof value === 'string') {
      return value;
    }
    return value.format('YYYY-MM-DD');
  };

  const loadEntries = async () => {
    try {
      setLoading(true);
      const response = await diaryAPI.getLabTestEntries();
      setEntries(sortDiaryEntriesNewestFirst((response.data || []) as any[]));
    } catch (error: any) {
      console.error('Error loading lab test entries:', error);
      message.error('Ошибка загрузки записей анализов');
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setModalVisible(false);
    setEditingId(null);
    form.resetFields();
    setPhotoFile(null);
  };

  const openAddModal = () => {
    setEditingId(null);
    form.setFieldsValue({ test_date: dayjs() });
    setPhotoFile(null);
    setModalVisible(true);
  };

  const handleEdit = (record: any) => {
    setEditingId(record.id);
    form.setFieldsValue({
      test_date: record.test_date ? dayjs(record.test_date) : dayjs(),
      test_type: record.test_type === 'Анализов не было' ? record.test_type : (record.test_type || undefined),
    });
    setPhotoFile(null);
    setModalVisible(true);
  };

  const onFinish = async (values: any) => {
    try {
      if (!editingId && !getUploadRawFile(photoFile)) {
        message.error('Пожалуйста, загрузите фото анализа');
        return;
      }

      const formData = new FormData();
      if (photoFile) {
        appendUploadToFormData(formData, 'photo', photoFile, 'lab-test.jpg');
      }
      formData.append('test_date', resolveTestDate(values.test_date));
      if (values.test_type) {
        formData.append('test_type', values.test_type);
      }

      if (editingId) {
        await diaryAPI.updateLabTestEntry(editingId, formData);
        message.success('Анализ успешно обновлен!');
      } else {
        await diaryAPI.createLabTestEntry(formData);
        message.success('Анализ успешно добавлен!');
      }
      closeModal();
      await loadEntries();
    } catch (error: any) {
      console.error('Error saving lab test entry:', error);
      message.error(error.response?.data?.detail || 'Ошибка сохранения анализа');
    }
  };

  const saveNoTestsEntry = async () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = 'transparent';
      ctx.fillRect(0, 0, 1, 1);
    }
    await new Promise<void>((resolve, reject) => {
      canvas.toBlob(async (blob) => {
        if (!blob) {
          reject(new Error('Не удалось создать файл'));
          return;
        }
        try {
          const file = new File([blob], 'no-tests.png', { type: 'image/png' });
          const formData = new FormData();
          formData.append('photo', file);
          formData.append('test_date', dayjs().format('YYYY-MM-DD'));
          formData.append('test_type', 'Анализов не было');
          await diaryAPI.createLabTestEntry(formData);
          message.success('Отмечено, что анализов не было');
          await loadEntries();
          resolve();
        } catch (error) {
          reject(error);
        }
      }, 'image/png');
    });
  };

  const columns = [
    {
      title: 'Дата и время',
      key: 'datetime',
      render: (_: any, record: any) => formatDiaryDateTime(record),
    },
    {
      title: 'Тип анализа',
      dataIndex: 'test_type',
      key: 'test_type',
      render: (text: string) => text || 'Лабораторный анализ',
    },
    {
      title: 'Фото',
      dataIndex: 'photo_url',
      key: 'photo_url',
      render: (url: string, record: any) => {
        if (record.test_type === 'Анализов не было') {
          return '-';
        }
        if (!url) return '-';
        const handleDownload = async () => {
          try {
            const response = await fileAPI.downloadFile(url);
            const blob = new Blob([response.data as BlobPart]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = getDownloadFileName(url, `lab_test_${record.test_date || 'photo'}`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
          } catch (error) {
            console.error('Error downloading file:', error);
            message.error('Ошибка скачивания файла');
          }
        };
        return (
          <Space>
            <Image
              src={getFilePreviewUrl(url)}
              width={50}
              height={50}
              style={{ objectFit: 'cover', cursor: 'pointer' }}
              preview={true}
            />
            <Button type="link" size="small" icon={<DownloadOutlined />} onClick={handleDownload}>
              Скачать
            </Button>
          </Space>
        );
      },
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_: any, record: any) => (
        <Button icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
      ),
    },
  ];

  const isMobile = window.innerWidth < 768;

  return (
    <div style={{ padding: isMobile ? '0' : '0' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <Title level={isMobile ? 3 : 2} style={{ color: '#263238', fontSize: isMobile ? '20px' : '24px' }}>
          Дневник анализов
        </Title>
      </div>

      <Card style={{ borderRadius: '12px', marginBottom: isMobile ? 16 : 24 }}>
        <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#7CB342', borderColor: '#7CB342', width: isMobile ? '100%' : 'auto' }}
            block={isMobile}
          >
            Добавить анализ
          </Button>
          <Button
            onClick={async () => {
              try {
                await saveNoTestsEntry();
              } catch (error: any) {
                console.error('Error saving no lab tests entry:', error);
                message.error(error.response?.data?.detail || 'Ошибка сохранения');
              }
            }}
            style={{ width: isMobile ? '100%' : 'auto' }}
            block={isMobile}
          >
            Анализов не было
          </Button>
        </Space>
      </Card>

      <Card style={{ borderRadius: '12px' }}>
        <Table columns={columns} dataSource={entries} loading={loading} rowKey="id" pagination={{ pageSize: 20 }} />
      </Card>

      <Modal
        title={editingId ? 'Редактировать анализ' : 'Добавить анализ'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{ test_date: dayjs() }}
        >
          <Form.Item
            label="Фото анализа"
            required={!editingId}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              accept="image/*"
              beforeUpload={() => false}
              fileList={photoFile ? [photoFile] : []}
              onChange={({ file, fileList }) => {
                if (file.status === 'removed' || fileList.length === 0) {
                  setPhotoFile(null);
                  return;
                }
                setPhotoFile(fileList[fileList.length - 1]);
              }}
              onRemove={() => setPhotoFile(null)}
            >
              {!photoFile && (
                <div>
                  <UploadOutlined /> {editingId ? 'Заменить фото' : 'Загрузить фото'}
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item label="Тип анализа (опционально)" name="test_type">
            <Input placeholder="Кровь, моча, УЗИ и т.д." />
          </Form.Item>

          <Form.Item
            label="Дата"
            name="test_date"
            rules={[{ required: true, message: 'Укажите дату анализа' }]}
          >
            <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" style={{ backgroundColor: '#7CB342', borderColor: '#7CB342' }}>
                {editingId ? 'Обновить' : 'Сохранить'}
              </Button>
              <Button onClick={closeModal}>
                Отмена
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
